import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Login from './pages/Login'
import AcceptInvite from './pages/AcceptInvite'
import VerifyEmail from './pages/VerifyEmail'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import ComponentShowcase from './pages/ComponentShowcase'
import { tokenManager } from './lib/api'
import { ToastProvider } from './components/ui'
import { UserProvider, useUser } from './contexts/UserContext'
import { AgentProvider } from './contexts/AgentContext'
import GlobalAgentDrawer, { AgentFloatingButton } from './components/ProgramAgent'
import CuiBanner from './components/ui/CuiBanner'
import DiscoveryProgress from './components/DiscoveryProgress'
import { usePageTracking } from './hooks/usePageTracking'
import { ProtectedRoute, SignedInOnly, FullPageSpinner } from './components/auth/ProtectedRoute'
import { homePathFor } from './lib/postLoginRedirect'

// Layouts
import PMDashboardLayout from './layouts/PMDashboardLayout'
import ProgramLayout from './layouts/ProgramLayout'
import ReviewerLayout from './layouts/ReviewerLayout'

// PM/Leader Pages
import AgentHome from './pages/AgentHome'
import ProgramAgentFullPage from './pages/ProgramAgentFullPage'
import Programs from './pages/Programs'
import ProgramOverview from './pages/program-tabs/ProgramOverview'
import WorkflowGraph from './pages/program-workflow/WorkflowGraph'
import ModulePage from './pages/program-workflow/ModulePage'
import ProgramRequirements from './pages/program-tabs/ProgramRequirements'
import ProgramDocumentation from './pages/program-tabs/ProgramDocumentation'
import ProgramInterviews from './pages/program-tabs/ProgramInterviews'
import ProgramVendors from './pages/program-tabs/ProgramVendors'
import ProgramSettings from './pages/program-tabs/ProgramSettings'
import ProgramMarketView from './pages/program-tabs/ProgramMarketView'
import ProgramSolutions from './pages/program-tabs/ProgramSolutions'
import ProgramCosting from './pages/program-tabs/ProgramCosting'
import ProgramCostRegister from './pages/program-tabs/ProgramCostRegister'
import VendorDetail from './pages/VendorDetail'

// Program Setup Subpages (reached from the program sidebar and top nav)
import ProcessView from './pages/program-setup/ProcessView'
import ScorecardFullView from './pages/program-setup/ScorecardFullView'
import TeamView from './pages/program-setup/TeamView'
import DocumentsView from './pages/program-setup/DocumentsView'
import ProgramDocumentTemplatesView from './pages/program-setup/ProgramDocumentTemplatesView'
import VendorMarketplace from './pages/VendorMarketplace'

// Market and Document Pages
import MarketView from './pages/MarketView'
import ProgramDocumentEditor from './pages/program-documents/ProgramDocumentEditor'

// Exec Dashboard
import ExecDashboard from './pages/ExecDashboard'
import PortfolioGroups from './pages/PortfolioGroups'
import Goals from './pages/Goals'
import ExecHome from './pages/ExecHome'
import ExecExplore from './pages/ExecExplore'
import ExecPathwayFlow from './pages/ExecPathwayFlow'
import ExecDIB from './pages/ExecDIB'
import ExecDIBExplore from './pages/ExecDIBExplore'
import ExecDIBDashboard from './pages/ExecDIBDashboard'
import ExecLayout from './layouts/ExecLayout'
import AcqPerformanceDashboard from './pages/AcqPerformanceDashboard'

// Settings Pages
import OfficeSettings from './pages/settings/OfficeSettings'
import OfficeWorkflows from './pages/settings/OfficeWorkflows'
import UserSettings from './pages/settings/UserSettings'

// Admin Console
import ConsoleLayout from './pages/console/ConsoleLayout'
import WorkflowDesigner from './pages/console/WorkflowDesigner'
import ConsoleDocumentTemplates from './pages/console/ConsoleDocumentTemplates'

// Reviewer Pages
import ReviewerTasks from './pages/reviewer/ReviewerTasks'
import ReviewerVendors from './pages/reviewer/ReviewerVendors'
import ReviewerEvaluation from './pages/reviewer/ReviewerEvaluation'

// Vendor Pages
import VendorLayout from './layouts/VendorLayout'
import VendorProfile from './pages/vendor/VendorProfile'
import VendorSubmissions from './pages/vendor/VendorSubmissions'
import NewSubmission from './pages/vendor/NewSubmission'
import SubmissionDetail from './pages/vendor/SubmissionDetail'
import OpportunityResponse from './pages/vendor/OpportunityResponse'
import VendorSettings from './pages/vendor/VendorSettings'
import TransitHome from './pages/transit/TransitHome'
import TransitLayout from './layouts/TransitLayout'
import TransitWorkspace from './pages/transit/TransitWorkspace'
import TransitSignIn from './pages/transit/TransitSignIn'
import TransitInvite from './pages/transit/TransitInvite'
import TransitPeople from './pages/transit/TransitPeople'

// Redirect leaders to /exec, everyone else to /home
function RoleBasedHome() {
  const { user, loading } = useUser()
  if (loading) return null
  if (user?.role === 'leader') return <Navigate to="/exec" replace />
  return <AgentHome />
}

// / is the login page: logged-out users get the form here, logged-in users are
// redirected by role. The marketing splash lives on its own site
// (Velocity-Defense/site), so the app has no public landing page.
function RootRoute() {
  if (!tokenManager.isAuthenticated()) {
    return <Login />
  }
  return <RoleBasedRedirect />
}

function RoleBasedRedirect() {
  const { user, loading } = useUser()
  if (loading) return <FullPageSpinner />
  return <Navigate to={homePathFor(user)} replace />
}

function PageTracker() {
  usePageTracking()
  return null
}

function App() {
  return (
    <ToastProvider>
      <CuiBanner />
      <UserProvider>
        <BrowserRouter>
          <AgentProvider>
            <PageTracker />
            <SignedInOnly>
              <GlobalAgentDrawer />
              <AgentFloatingButton />
              <DiscoveryProgress />
            </SignedInOnly>
            <Routes>
          {/* Entry points */}
          <Route path="/" element={<RootRoute />} />
          {/* The login form lives at /. Keep /login as a redirect so bookmarks
              and links in already-sent emails don't 404. */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/verify-email/:uidb64/:token" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:uidb64/:token" element={<ResetPassword />} />
          <Route path="/showcase" element={<ComponentShowcase />} />

          {/* PM Dashboard Routes — Agent nav (Home, Programs, Market) */}
          <Route
            element={
              <ProtectedRoute>
                <PMDashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/home" element={<RoleBasedHome />} />
            <Route path="/programs" element={<Programs />} />
            {/* Retired chat-only creation page — programs start from a workflow on /home. */}
            <Route path="/programs/new" element={<Navigate to="/home" replace />} />
            <Route path="/market" element={<MarketView />} />
            <Route path="/settings/office" element={<OfficeSettings />} />
            {/* Office workflow customization (DEV-732): list + full-screen designer */}
            <Route
              path="/settings/office/workflows"
              element={
                <div className="p-8">
                  <div className="max-w-5xl mx-auto">
                    <OfficeWorkflows />
                  </div>
                </div>
              }
            />
            <Route path="/settings/office/workflows/:workflowId" element={<WorkflowDesigner basePath="/settings/office/workflows" />} />
            <Route path="/settings/user" element={<UserSettings />} />
            <Route path="/acq-dashboard" element={<AcqPerformanceDashboard />} />
          </Route>

          {/* Transit: transition packages, served by the Go service (overmatch/go).
              Transit signs its users in itself, so these sit outside the Django
              ProtectedRoute: TransitLayout sends anyone without a session to sign-in. */}
          <Route path="/transit/sign-in" element={<TransitSignIn />} />
          <Route path="/transit/invite/:token" element={<TransitInvite />} />
          <Route element={<TransitLayout />}>
            <Route path="/transit" element={<TransitHome />} />
            <Route path="/transit/people" element={<TransitPeople />} />
            <Route path="/transit/:transitionId" element={<TransitWorkspace />} />
          </Route>

          {/* Exec Routes — Exec nav (Dashboard, Goals, Portfolios) */}
          <Route
            element={
              <ProtectedRoute>
                <ExecLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/exec" element={<ExecHome />} />
            <Route path="/exec/dashboard" element={<ExecDashboard />} />
            <Route path="/exec/goals" element={<Goals />} />
            <Route path="/exec/portfolio-groups" element={<PortfolioGroups />} />
            <Route path="/exec/explore" element={<ExecExplore />} />
            <Route path="/exec/explore/flow" element={<ExecPathwayFlow />} />
            <Route path="/exec/dib" element={<ExecDIB />} />
            <Route path="/exec/dib/explore" element={<ExecDIBExplore />} />
            <Route path="/exec/dib/dashboard" element={<ExecDIBDashboard />} />
          </Route>

          {/* Admin Console (superuser-only) */}
          <Route
            path="/console"
            element={
              <ProtectedRoute>
                <ConsoleLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ConsoleDocumentTemplates />} />
            <Route path="document-templates" element={<ConsoleDocumentTemplates />} />
            <Route path="workflows" element={<WorkflowDesigner />} />
            <Route path="workflows/:workflowId" element={<WorkflowDesigner />} />
          </Route>

          {/* Program-Specific Routes (WITH sidebar) */}
          <Route
            path="/programs/:programId"
            element={
              <ProtectedRoute>
                <ProgramLayout />
              </ProtectedRoute>
            }
          >
            {/* Composable workflows (DEV-732): the module graph is the program
                landing surface; each module owns its own page. */}
            <Route index element={<WorkflowGraph />} />
            <Route path="module/:stepKey" element={<ModulePage />} />
            <Route path="overview" element={<ProgramOverview />} />

            {/* Program Setup Reference Pages (accessed via sidebar) */}
            <Route path="setup/process" element={<ProcessView />} />
            <Route path="setup/scorecards" element={<ScorecardFullView />} />
            <Route path="setup/team" element={<TeamView />} />
            <Route path="setup/documents" element={<ProgramDocumentTemplatesView />} />
            <Route path="setup/documents-legacy" element={<DocumentsView />} />
            
            {/* Market */}
            <Route path="solutions" element={<ProgramSolutions />} />
            <Route path="market" element={<ProgramMarketView />} />
            <Route path="costing" element={<ProgramCosting />} />
            <Route path="cost-register" element={<ProgramCostRegister />} />

            {/* Vendors */}
            <Route path="vendors" element={<ProgramVendors />} />
            <Route path="vendors/marketplace" element={<VendorMarketplace />} />
            <Route path="vendors/:vendorId" element={<VendorDetail />} />
            
            {/* Documents — generated program documents (market research, CNS, etc.) */}
            <Route path="documents/:programDocumentId" element={<ProgramDocumentEditor />} />
            
            {/* Interviews */}
            <Route path="interviews" element={<ProgramInterviews />} />

            {/* Full-page agent chat, program-scoped */}
            <Route path="agent" element={<ProgramAgentFullPage />} />

            {/* Legacy Routes */}
            <Route path="contracting" element={<div className="p-8"><h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-4">Contracting</h1><p className="text-slate-600 dark:text-slate-400">Contracting workflow for sole-source acquisitions (Coming Soon)</p></div>} />
            <Route path="requirements" element={<ProgramRequirements />} />
            <Route path="documentation" element={<ProgramDocumentation />} />
            <Route path="settings" element={<ProgramSettings />} />
          </Route>

          {/* Reviewer Routes */}
          <Route
            path="/review"
            element={
              <ProtectedRoute>
                <ReviewerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/review/tasks" replace />} />
            <Route path="tasks" element={<ReviewerTasks />} />
            <Route path="vendors" element={<ReviewerVendors />} />
            <Route path="vendors/:vendorId" element={<VendorDetail />} />
            <Route path="evaluate/:programId/:vendorId" element={<ReviewerEvaluation />} />
          </Route>

          {/* Vendor Submission Routes */}
          <Route
            path="/submit"
            element={
              <ProtectedRoute>
                <VendorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<VendorProfile />} />
            <Route path="submissions" element={<VendorSubmissions />} />
            <Route path="new/:programId" element={<NewSubmission />} />
            <Route path=":submissionId" element={<SubmissionDetail />} />
            <Route path="opportunities/:opportunityId" element={<OpportunityResponse />} />
            <Route path="settings" element={<VendorSettings />} />
          </Route>
          </Routes>
          </AgentProvider>
        </BrowserRouter>
      </UserProvider>
    </ToastProvider>
  )
}

export default App
